import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { ErrorBox, Loading, PageHead, toast } from '../components/ui';
import { duration, dueLabel, errorMessage, pct } from '../format';
import { ERROR, KIND, RATING } from '../labels';
import type { ErrorType, NextResponse } from '../types';

type Phase = 'recall' | 'check' | 'why';
const MAX_MS = 10 * 60 * 1000;

export function Review() {
  const [data, setData] = useState<NextResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('recall');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<{ reviewId: number; rating: number; label: string } | null>(null);
  const [session, setSession] = useState({ count: 0, again: 0 });
  const shownAt = useRef(Date.now());
  const answerRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    try {
      const next = await api.next();
      setData(next);
      setError(null);
      setPhase('recall');
      setTyped('');
      shownAt.current = Date.now();
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (phase === 'recall') answerRef.current?.focus();
  }, [phase, data]);

  const submit = useCallback(
    async (rating: number, errorType: ErrorType | null = null) => {
      if (!data?.card || busy) return;
      setBusy(true);
      try {
        const res = await api.submit(data.card.id, {
          rating,
          typed_answer: typed,
          duration_ms: Math.min(Date.now() - shownAt.current, MAX_MS),
          error_type: errorType,
        });
        setLast({ reviewId: res.review_id, rating, label: `'${RATING[rating - 1].label}' → ${duration(res.interval_seconds)} 뒤 다시` });
        setSession((s) => ({ count: s.count + 1, again: s.again + (rating === 1 ? 1 : 0) }));
        await load();
      } catch (e) {
        toast(errorMessage(e), 'err');
      } finally {
        setBusy(false);
      }
    },
    [data, typed, busy, load],
  );

  const undo = async () => {
    if (!last) return;
    try {
      await api.undo(last.reviewId);
      setSession((s) => ({ count: Math.max(0, s.count - 1), again: Math.max(0, s.again - (last.rating === 1 ? 1 : 0)) }));
      setLast(null);
      toast('채점을 되돌렸습니다');
      await load();
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (phase === 'recall' && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setPhase('check');
        return;
      }
      const tag = (e.target as HTMLElement).tagName;
      if (phase === 'check' && tag !== 'TEXTAREA' && tag !== 'INPUT' && ['1', '2', '3', '4'].includes(e.key)) {
        const r = Number(e.key);
        if (r === 1) setPhase('why');
        else void submit(r);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, submit]);

  if (error) return <ErrorBox message={error} onRetry={() => void load()} />;
  if (!data) return <Loading />;

  const c = data.counts;
  const head = (
    <PageHead
      title="복습"
      sub={
        <>
          학습 중 <b>{c.learning}</b> · 복습 <b>{c.review}</b> · 새 카드 <b>{c.new}</b>
          {session.count > 0 && ` · 이번에 ${session.count}장 (다시 ${session.again})`}
        </>
      }
      actions={<a className="btn btn-ghost btn-sm" href="#/guide/memory">오래 기억하는 법</a>}
    />
  );
  const undoBar = last && (
    <div className="undo-bar">
      <span>{last.label}</span>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => void undo()}>되돌리기</button>
    </div>
  );

  if (data.done || !data.card) {
    return (
      <>
        {head}
        {undoBar}
        <div className="panel done-panel">
          <h2>지금 복습할 카드를 모두 마쳤습니다</h2>
          <p>오늘 {data.today.reviewed}장 · '다시' {data.today.again}장 · {data.today.minutes}분</p>
          {data.next_due && <p className="muted">다음 카드: {dueLabel(data.next_due)}</p>}
          {c.new_waiting > 0 && <p className="muted small">새 카드 {c.new_waiting}장은 하루 한도와 '같은 개념은 하루 하나' 규칙 때문에 다음에 나옵니다.</p>}
          <div className="row-gap">
            <a className="btn" href="#/knowledge/new">새 개념 추가</a>
            <a className="btn btn-primary" href="#/">홈으로</a>
          </div>
        </div>
      </>
    );
  }

  const card = data.card;
  return (
    <>
      {head}
      {undoBar}
      <article className="panel review-card" aria-live="polite">
        <div className="review-meta">
          <span className="badge accent">{KIND[card.kind]}</span>
          {data.item && (
            <a className="muted small" href={`#/knowledge/${data.item.id}`} target="_blank" rel="noreferrer">
              {data.item.title}{data.item.domain && ` · ${data.item.domain}`}
            </a>
          )}
          <span className="muted small push-right">
            {card.is_new ? '첫 회상' : card.recall != null ? `예상 기억 확률 ${pct(card.recall)}` : '다시 확인'}
          </span>
        </div>
        <h2 className="prompt">{card.prompt}</h2>
        {phase === 'recall' ? (
          <>
            <label className="sr-only" htmlFor="answer">내 답</label>
            <textarea
              id="answer"
              ref={answerRef}
              className="input answer-input"
              rows={5}
              placeholder="보지 말고, 떠오르는 대로 써 보세요. (Ctrl+Enter = 답 확인)"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
            <div className="row-gap">
              <button type="button" className="btn btn-primary" onClick={() => setPhase('check')}>답 확인</button>
              {!typed.trim() && <span className="muted small">써 보고 확인할수록 오래 남습니다</span>}
            </div>
          </>
        ) : (
          <>
            <div className="compare">
              <div>
                <h3 className="sub-title">내 답</h3>
                <div className="answer-box">{typed.trim() || <span className="muted">(쓰지 않고 떠올리기만 함)</span>}</div>
              </div>
              <div>
                <h3 className="sub-title">기록해 둔 답</h3>
                <div className="answer-box ref">{card.answer}</div>
              </div>
            </div>
            {phase === 'check' ? (
              <div className="ratings" role="group" aria-label="채점">
                {RATING.map((r) => (
                  <button key={r.v} type="button" className={`rate ${r.tone}`} disabled={busy} onClick={() => (r.v === 1 ? setPhase('why') : void submit(r.v))}>
                    <b>{r.label}</b>
                    <span>{r.hint}</span>
                    <span className="rate-next">{data.previews ? duration(data.previews[String(r.v)]) : ''}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="why">
                <p>
                  <b>어떤 실수였나요?</b> <span className="muted small">약점 진단에 쓰입니다</span>
                </p>
                <div className="chips">
                  {(Object.keys(ERROR) as ErrorType[]).map((t) => (
                    <button key={t} type="button" className="chip" disabled={busy} onClick={() => void submit(1, t)}>
                      {ERROR[t]}
                    </button>
                  ))}
                </div>
                <div className="row-gap">
                  <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void submit(1)}>이유 없이 '다시'</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPhase('check')}>채점으로 돌아가기</button>
                </div>
              </div>
            )}
          </>
        )}
      </article>
      <p className="muted small center">단축키: Ctrl+Enter 답 확인 · 1~4 채점</p>
    </>
  );
}
